import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { MapContainer } from 'react-leaflet';
import L, { Zoom } from 'leaflet';
import { FormControlLabel, Switch } from '@mui/material';
import {
  Button,
  LSKeys,
  Loader,
  QueryBoundary,
  useLocalStorage,
} from '@outdoria/ui';
import { ActionBar } from '../../containers';
import { DEFAULT_COORDS } from '../../utils/defaults';
import { InteractiveMap, MarkerCreate, MarkerEdit } from './components';

import s from './styles.module.scss';

const MAP_OPTIONS = {
  zoom: 16,
  minZoom: 1,
  maxZoom: 20,
  dragging: true,
  scrollWheelZoom: 'center' as Zoom,
  style: { cursor: 'crosshair' },
};

export const MapView = () => (
  <QueryBoundary fallback={<Loader />}>
    <Load />
  </QueryBoundary>
);

const Load = () => {
  const geo = useMemo(() => navigator.geolocation, []);

  const [isSatelliteEnabled, setIsSatelliteEnabled] = useState(false);
  const [isCreateView, setIsCreateView] = useState(false);

  const [coords, setCoords] = useLocalStorage(
    LSKeys.OUTDORIA_LAST_KNOWN_LOCATION,
    DEFAULT_COORDS,
  );
  const [selectedCoords, setSelectedCoords] =
    useState<[number, number]>(coords);
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);

  const onCenterMapOnMe = () => {
    toast.info('Centering on your current position...');
    geo.getCurrentPosition(
      (position: GeolocationPosition) => {
        const { latitude, longitude } = position.coords;
        setCoords([latitude, longitude]);
      },
      error => {
        console.error(`ERROR(${error.code}): ${error.message}`);
      },
    );
  };

  const center = L.latLng(coords[0], coords[1]);

  // MapContainer needs to wrap a component which calls useLeafletMap
  // since it has to be within the context.
  return (
    <div className={s.view}>
      <ActionBar
        title={`Selected marker ID: ${selectedMarker ?? '-'}`}
        buttons={
          <>
            <CheckmarkSatellite
              value={isSatelliteEnabled}
              setNewValue={setIsSatelliteEnabled}
            />
            <Button label="Center on me" onClick={onCenterMapOnMe} />
          </>
        }
      />

      <MapContainer {...MAP_OPTIONS} className={s.mapContainer} center={center}>
        <InteractiveMap
          coords={coords}
          selectedCoords={selectedCoords}
          selectedMarker={selectedMarker}
          setSelectedMarker={setSelectedMarker}
          setSelectedCoords={setSelectedCoords}
          setIsCreateView={setIsCreateView}
          isCreateView={isCreateView}
          isSatelliteEnabled={isSatelliteEnabled}
        />
      </MapContainer>

      {selectedMarker && (
        <MarkerEdit
          selectedMarker={selectedMarker}
          onExit={() => {
            setSelectedMarker(null);
          }}
        />
      )}

      {!selectedMarker && isCreateView && (
        <MarkerCreate
          selectedCoords={selectedCoords}
          onExit={() => setIsCreateView(false)}
        />
      )}
    </div>
  );
};

type CheckmarkSatelliteProps = {
  value: boolean;
  setNewValue: (newValue: boolean) => void;
};
const CheckmarkSatellite = ({
  value,
  setNewValue,
}: CheckmarkSatelliteProps) => (
  <FormControlLabel
    label="Satellite view"
    control={
      <Switch
        defaultChecked={false}
        value={value}
        color="default"
        onChange={(_, checked) => setNewValue(checked)}
      />
    }
  />
);
